import _                             from 'underscore';
import moment, { unitOfTime }        from 'moment/moment';
import { ModalProps, SemanticICONS } from 'semantic-ui-react';

import { CorvalActivityHire, CorvalColor, CorvalCompletionState, CorvalCriticality, CorvalProjectActivity, CorvalRecurrenceType, CorvalStaticProjectActivity, FuncOrAsyncFunc, SemanticUIColor, Uuid } from '../types';

import { ProjectService, TimeService }                                                                    from '../services';
import { ACTIVITY_CRITICALITY, ACTIVITY_STATUS, ACTIVITY_STATUS_INFO, ENTITY_COLORIZATION, ENTITY_ICONS } from '../constants/ProductEnums';
import { ProjectActivityUpdateChangeSet }                                                                 from '../views/pages/projects/activities/ProjectActivityUpdateDialog';
import { TIME_UNITS }                                                                                     from '../constants/TimeEnums';
import { withoutType }                                                                                    from '../store/_apolloUtil';
import { ProjectTimelineDateFieldObject }                                                                 from '../services/ProjectService';
import React                                                                                              from "react";
import { useSystemModals }                                                                                from "@src/hooks/useSystem";
import { StaticProjectActivityBulkUpdateDialog, SupportedBulkUpdateFields }                               from "@src/views/pages/projects/operations/StaticProjectActivityBulkUpdateDialog";
import { StaticProjectActivityBulkUpdateMultiDialog }                                                     from "@src/views/pages/projects/operations/StaticProjectActivityBulkUpdateMultiDialog";
import { getFieldWithProjectIdAccessor }                                                                  from "@src/util";
import { useApolloClient }                                                                                from "@apollo/react-hooks";
import { DecisionEngineQueries }                                                                          from "@src/queries";


export interface ProjectActivityChangeDelta<T> {
    projectActivityId: Uuid,
    oldValue: T,
    newValue: T,
}

export interface ProjectActivityStatusChangeDelta extends ProjectActivityChangeDelta<ACTIVITY_STATUS> {}

export type ProjectActivityEntityChangeSet = {
    added: CorvalStaticProjectActivity[],
    updated: CorvalStaticProjectActivity[],
    removed: CorvalStaticProjectActivity[],
}

export type ProjectActivityEntityChangeCallback = (
    updatedAttributes: ProjectActivityUpdateChangeSet,
    projectActivityId: Uuid,
    propagationCalled?: boolean,
    propagationResultsData?: any,
    entityChangeSet?: ProjectActivityEntityChangeSet
) => void;

export type StaticProjectActivityEntityChangeCallback = (
    updatedAttributes: ProjectActivityUpdateChangeSet,
    projectActivityId: Uuid,
) => void;

export type ProjectActivityEntitySetChangeCallback = (
    updatedAttributes: Record<Uuid, Partial<CorvalStaticProjectActivity>>
) => void;

export type ProjectActivityStatusChangeCallback = ( deltas: ProjectActivityStatusChangeDelta[] ) => void;

export interface UseProjectActivityHook {

    hasTransitionFromExcluded( delta: ProjectActivityStatusChangeDelta ): boolean

    hasTransitionToExcluded( delta: ProjectActivityStatusChangeDelta ): boolean

    statusTransitionIsActionable( delta: ProjectActivityStatusChangeDelta ): boolean

    statusTransitionSetHasActionableItems( deltas: ProjectActivityStatusChangeDelta[] ): boolean

    getProjectActivityTags( item: Partial<CorvalStaticProjectActivity> ): string[],

    getProjectActivityHireTypes( item: Partial<CorvalStaticProjectActivity> ): string[],

    getProjectActivityDuration( item: Partial<CorvalStaticProjectActivity>, asDiscreteObject?: boolean ): string | { durationValue: number, durationUnit: TIME_UNITS },

    hasLateCompletion( endDate: string, proposeEndDate: string | null, projectTimelineDateFieldObjects?: ProjectTimelineDateFieldObject[] ): boolean,

    isCriticalActivity( criticalityValue: CorvalCriticality | ACTIVITY_CRITICALITY ): boolean,

    getCriticalityIcon( criticalityValue: CorvalCriticality | ACTIVITY_CRITICALITY ): SemanticICONS,

    getCriticalityColor( criticalityValue: CorvalCriticality | ACTIVITY_CRITICALITY ): CorvalColor,

    getProjectActivityStatusInfo( item: Pick<Partial<CorvalStaticProjectActivity>, 'completionState'> ): {
        isNotStarted: boolean,
        isDeferred: boolean,
        isComplete: boolean,
        isInProgress: boolean,
        activityActiveColor: CorvalColor,
        defaultActivityColor: CorvalColor,
        statusColor: CorvalColor,
        statusIcon: SemanticICONS,
        statusLabel: string,
        statusValue: ACTIVITY_STATUS,
    },

    getStatusIcon( completionState: CorvalCompletionState | ACTIVITY_STATUS ): SemanticICONS,

    getStatusColor( completionState: CorvalCompletionState | ACTIVITY_STATUS ): CorvalColor,

    getChangeSetProperties(
        changeSetType: 'recurrences' | 'activityTimingDisplay' | 'activityResourcing' | 'activityTiming',
        projectId?: Uuid,
    ): string,

    bulkUpdateProjectActivities(
        projectId: Uuid,
        projectVersionId: Uuid | null,
        projectActivityIds: Uuid[],
        type: SupportedBulkUpdateFields,
        onUpdateComplete?: FuncOrAsyncFunc<[ mutatedActivitySet: Array<CorvalStaticProjectActivity> ], void>,
        onUpdateError?: FuncOrAsyncFunc<[ e: Error ], void>,
        modalProps?: ModalProps
    ): void

    bulkUpdateProjectActivitiesMulti(
        projectId: Uuid,
        projectVersionId: Uuid | null,
        projectActivityIds: Uuid[],
        onUpdateComplete?: FuncOrAsyncFunc<[ mutatedActivitySet: Array<CorvalStaticProjectActivity> ], void>,
        onUpdateError?: FuncOrAsyncFunc<[ e: Error ], void>,
        showOptionsForTypes?: Array<SupportedBulkUpdateFields> | boolean,
        modalProps?: ModalProps
    ): void,

    getProjectActivityHasDependencyLoops(
        activityId: Uuid,
        parentIds: Uuid[],
        childIds: Uuid[],
    ): Promise<boolean | null>
}

export const useProjectActivities = (): UseProjectActivityHook => {

    const modalActions = useSystemModals(),
          apolloClient = useApolloClient();

    const hasTransitionFromExcluded: UseProjectActivityHook['hasTransitionFromExcluded'] = ( delta ) => {

        return (
            delta.oldValue === ACTIVITY_STATUS.EXCLUDED &&
            delta.newValue !== ACTIVITY_STATUS.EXCLUDED );
    }

    const hasTransitionToExcluded: UseProjectActivityHook['hasTransitionToExcluded'] = ( delta ) => {

        return (
            delta.oldValue !== ACTIVITY_STATUS.EXCLUDED &&
            delta.newValue === ACTIVITY_STATUS.EXCLUDED );
    }

    const statusTransitionIsActionable: UseProjectActivityHook['statusTransitionIsActionable'] = ( delta ) => {

        return (
            hasTransitionFromExcluded( delta ) ||
            hasTransitionToExcluded( delta )
        )
    }

    const statusTransitionSetHasActionableItems: UseProjectActivityHook['statusTransitionSetHasActionableItems'] = ( deltas ) => {

        let results = _.compact( deltas.map( statusTransitionIsActionable ) )

        return ( results.length > 0 );
    }

    // hireType info
    const getProjectActivityHireTypes: UseProjectActivityHook['getProjectActivityHireTypes'] = ( item ) => {

        let hireTypes             = new Set(),
            extractHireTypeMethod = ( hireTypeEntity: CorvalActivityHire ) => hireTypes.add( hireTypeEntity.hireTypeId );

        if( item.requiredHires && _.isArray( item.requiredHires ) ){

            // Required hires: Either DEM or user defined activity hires
            item.requiredHires.map( extractHireTypeMethod );
        }

        return Array.from( hireTypes ) as string[];
    }

    // tags info
    // @TODO: PA tags not support just yet, remove ignores once it is.
    const getProjectActivityTags: UseProjectActivityHook['getProjectActivityTags'] = ( item ) => {

        let tags             = new Set(),
            extractTagMethod = tagEntity => {
                if( tagEntity?.tag?.name ){
                    tags.add( tagEntity.tag.name );
                }
            };

        //@ts-ignore
        if( item.tags && _.isArray( item.tags ) ){

            // Project Activity tags (which could be curated)
            //@ts-ignore
            item.tags.map( extractTagMethod );

        } else if( item.activity && item.activity.tags && _.isArray( item.activity.tags ) ){

            // Activity tags (which come from the DEM)
            item.activity.tags.map( extractTagMethod );
        }
        return Array.from( tags ) as string[];
    }

    // Duration in readable format
    const getProjectActivityDuration: UseProjectActivityHook['getProjectActivityDuration'] = ( item, asDiscreteObject = false ) => {

        const durationValue  = TimeService.getDayDifferential(
                  TimeService.localDateToMoment( item?.startDate ),
                  TimeService.localDateToMoment( item?.endDate )
              ) ?? null,
              durationUnit   = TIME_UNITS.DAYS,
              discreteObject = { durationValue, durationUnit } as { durationValue: number, durationUnit: TIME_UNITS };

        return ( asDiscreteObject )
               ? discreteObject
               : ( durationValue && durationUnit )
                 ? `${ durationValue } ${ TimeService.getHumanizedTimeUnits( durationValue, durationUnit ) }`
                 : ``;
    }

    const hasLateCompletion: UseProjectActivityHook['hasLateCompletion'] = ( endDate, proposeEndDate, projectTimelineDateFieldObjects = [] ) => {

        const actualCompletionTime          = TimeService.localDateToMoment( proposeEndDate ),
              proposedCompletionValue       = TimeService.localDateToMoment( endDate ),
              proposedCompletionIsMilestone = !( proposedCompletionValue.isValid() );

        // return if no valid objects to compare
        if( _.isEmpty( proposeEndDate ) || _.isEmpty( endDate ) || !actualCompletionTime.isValid() ) return false;

        try{

            // If we have a milestone date field object, then we need to
            // compare the actual completion time to the milestone date.
            // Otherwise, we have a date field object to compare to.
            if( proposedCompletionIsMilestone ){

                const milestoneDateFieldObject = ProjectService.getTimelineDataObjectFromSet( endDate, projectTimelineDateFieldObjects );

                return milestoneDateFieldObject
                       ? actualCompletionTime.isAfter( TimeService.localDateToMoment( milestoneDateFieldObject.rawValue ) )
                       : false;

            } else {

                return actualCompletionTime.isAfter( proposedCompletionValue );
            }

        } catch( e ) {

            // ...
        }

        return false;
    }

    const isCriticalActivity: UseProjectActivityHook['isCriticalActivity'] = ( criticalityValue: any ) => {

        try{

            return ( ( criticalityValue as unknown as ACTIVITY_CRITICALITY ) == ACTIVITY_CRITICALITY.CRITICAL )

        } catch( e ) {

            // ...
        }

        return false;
    }

    const getCriticalityIcon: UseProjectActivityHook['getCriticalityIcon'] = ( criticalityValue: CorvalCriticality | ACTIVITY_CRITICALITY ) => {

        const mapping = new Map<CorvalCriticality | ACTIVITY_CRITICALITY, SemanticICONS>( [
            [ ACTIVITY_CRITICALITY.CRITICAL, ENTITY_ICONS.ACTIVITIES.CRITICALITY.CRITICAL ],
            [ ACTIVITY_CRITICALITY.PRIORITY, ENTITY_ICONS.ACTIVITIES.CRITICALITY.PRIORITY ],
            [ ACTIVITY_CRITICALITY.MODERATE, ENTITY_ICONS.ACTIVITIES.CRITICALITY.MODERATE ],
            [ ACTIVITY_CRITICALITY.MINOR, ENTITY_ICONS.ACTIVITIES.CRITICALITY.MINOR ],
            [ CorvalCriticality.Critical, ENTITY_ICONS.ACTIVITIES.CRITICALITY.CRITICAL ],
            [ CorvalCriticality.Priority, ENTITY_ICONS.ACTIVITIES.CRITICALITY.PRIORITY ],
            [ CorvalCriticality.Moderate, ENTITY_ICONS.ACTIVITIES.CRITICALITY.MODERATE ],
            [ CorvalCriticality.Minor, ENTITY_ICONS.ACTIVITIES.CRITICALITY.MINOR ],
        ] );

        return mapping.has( criticalityValue )
               ? mapping.get( criticalityValue )
               : mapping.get( criticalityValue )
    }

    const getCriticalityColor: UseProjectActivityHook['getCriticalityColor'] = ( criticalityValue: CorvalCriticality | ACTIVITY_CRITICALITY ) => {

        const mapping = new Map<CorvalCriticality | ACTIVITY_CRITICALITY, CorvalColor>( [
            [ ACTIVITY_CRITICALITY.CRITICAL, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.CRITICAL ],
            [ ACTIVITY_CRITICALITY.PRIORITY, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.PRIORITY ],
            [ ACTIVITY_CRITICALITY.MODERATE, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.MODERATE ],
            [ ACTIVITY_CRITICALITY.MINOR, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.MINOR ],
            [ CorvalCriticality.Critical, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.CRITICAL ],
            [ CorvalCriticality.Priority, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.PRIORITY ],
            [ CorvalCriticality.Moderate, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.MODERATE ],
            [ CorvalCriticality.Minor, ENTITY_COLORIZATION.ACTIVITIES.CRITICALITY.MINOR ],
        ] );

        return mapping.has( criticalityValue )
               ? mapping.get( criticalityValue )
               : mapping.get( criticalityValue )
    }

    const getProjectActivityStatusInfo: UseProjectActivityHook['getProjectActivityStatusInfo'] = ( activity ) => {

        let { VALUE: statusValue, ICON: statusIcon, COLOR: statusColor, LABEL: statusLabel } = ACTIVITY_STATUS_INFO[ activity.completionState ],
            isNotStarted                                                                     = ( activity.completionState as unknown as ACTIVITY_STATUS ) === ACTIVITY_STATUS.NOTSTARTED,
            isDeferred                                                                       = ( activity.completionState as unknown as ACTIVITY_STATUS ) === ACTIVITY_STATUS.EXCLUDED,
            isComplete                                                                       = ( activity.completionState as unknown as ACTIVITY_STATUS ) === ACTIVITY_STATUS.COMPLETE,
            isInProgress                                                                     = !( isComplete || isDeferred || isNotStarted )

        const defaultActivityColor = statusColor,
              activityActiveColor  = SemanticUIColor.ORANGE;

        return {
            isNotStarted,
            isDeferred,
            isComplete,
            isInProgress,
            activityActiveColor,
            defaultActivityColor,
            statusColor,
            statusIcon,
            statusLabel,
            statusValue: ( statusValue as ACTIVITY_STATUS ),
        }
    }

    const getStatusIcon: UseProjectActivityHook['getStatusIcon'] = ( completionState ) => {

        const { ICON: statusIcon } = ACTIVITY_STATUS_INFO[ completionState ];

        return statusIcon;
    };

    const getStatusColor: UseProjectActivityHook['getStatusColor'] = ( completionState ) => {

        const { COLOR: statusColor } = ACTIVITY_STATUS_INFO[ completionState ];

        return statusColor;
    };

    const getChangeSetProperties: UseProjectActivityHook['getChangeSetProperties'] = ( changeSetType, projectId ) => {

        const propertyMap = {
            activityTimingDisplay: `
                perceivedAsContinuous`,

            activityResourcing: `
                requiredHires {
				   headcount
				   hireType { 
				        id 
				        ${ getFieldWithProjectIdAccessor( projectId, 'name' ) }
				        ${ getFieldWithProjectIdAccessor( projectId, 'description' ) } 
				        businessFunction { id }
				        salary 
				        modifier 
                    }
				}`,

            activityTiming: `
                startTimeUtc
                endTimeUtc
                displayName
                completionState
                activityId
                isRecurrence
                childProjectActivities{
                    id activity {
                        id
                        parentActivities{
                            parentActivityId
                            parentCompletionRequired
                            parentSchedulingEnforced
                        }
                    }
                }`,

            budget: `
                requiredHires {
				   headcount
				   hireType { 
				        id 
				        ${ getFieldWithProjectIdAccessor( projectId, 'name' ) }
				        ${ getFieldWithProjectIdAccessor( projectId, 'description' ) }
				        businessFunction { id }
				        salary 
				        modifier 
                    }
				}
                operationalCostOverride
				operationalCostOverrideNote
				ongoingCostOverride
				ongoingCostOverrideNote
				professionalFees {
					id
					feeDate
					amount
					professionalFee{
						id
						name
						description
						resourceCode
						amount
					}
				}`
        };

        return propertyMap[ changeSetType ] ?? null;
    }

    const bulkUpdateProjectActivities: UseProjectActivityHook['bulkUpdateProjectActivities'] = ( projectId, projectVersionId, projectActivityIds, type, onUpdateComplete = null, onUpdateError = null, modalProps = {} ) => {

        modalActions.show(
            <StaticProjectActivityBulkUpdateDialog
                onUpdateComplete={ onUpdateComplete }
                onUpdateError={ onUpdateError }
                updateType={ type }
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                projectActivityIds={ projectActivityIds }
            />, {
                size: 'small',
                ...modalProps
            }
        );
    };

    const bulkUpdateProjectActivitiesMulti: UseProjectActivityHook['bulkUpdateProjectActivitiesMulti'] = ( projectId, projectVersionId, projectActivityIds, onUpdateComplete = null, onUpdateError = null, showOptionsForTypes = true, modalProps = {} ) => {

        modalActions.show(
            <StaticProjectActivityBulkUpdateMultiDialog
                onUpdateComplete={ onUpdateComplete }
                onUpdateError={ onUpdateError }
                projectId={ projectId }
                projectVersionId={ projectVersionId }
                projectActivityIds={ projectActivityIds }
                showOptionsForTypes={ showOptionsForTypes }
            />, {
                size:  'fullscreen',
                style: { maxWidth: `80vw` },
                ...modalProps
            }
        );
    };

    const getProjectActivityHasDependencyLoops: UseProjectActivityHook['getProjectActivityHasDependencyLoops'] = async ( activityId, parentIds, childIds ) => {

        if( _.isEmpty( activityId ) || _.isEmpty( parentIds ) || _.isEmpty( childIds ) ) return null;

        const loopCheckReq = await apolloClient.query( {
            query:       DecisionEngineQueries.queries.CHECK_ACTIVITY_DEPENDENCY_LOOP_DETECTION(),
            variables:   {
                id:                activityId,
                parentActivityIds: parentIds,
                childActivityIds:  childIds,
                tracePaths:        true,
            },
            fetchPolicy: 'network-only',
        } )

        try{

            const { loopsDetected } = loopCheckReq.data?.activityRelationshipLoops;

            return loopsDetected as boolean;

        } catch( e ) {

            // ...
        }

        return null;
    }

    return {
        hasTransitionFromExcluded,
        hasTransitionToExcluded,
        statusTransitionIsActionable,
        statusTransitionSetHasActionableItems,
        getProjectActivityTags,
        getProjectActivityDuration,
        hasLateCompletion,
        isCriticalActivity,
        getCriticalityIcon,
        getCriticalityColor,
        getProjectActivityStatusInfo,
        getProjectActivityHireTypes,
        getStatusIcon,
        getStatusColor,
        getChangeSetProperties,
        bulkUpdateProjectActivities,
        bulkUpdateProjectActivitiesMulti,
        getProjectActivityHasDependencyLoops,
    }
};
